import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import {
  Button,
  Grid,
  Typography,
  Stack,
  Card,
  CardContent,
  Box,
  IconButton,
} from '@mui/material';
import { useFieldArray, useForm } from 'react-hook-form';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import Loading from '../table-elements/loading.component';
import SnackbarInfo from '../common/snackbar-info.component';
import ROUTE_PATH from '../../config/routes.config';
import {
  deleteQuizQuestionById,
  getQuestionsByQuizId,
  manageQuizQuestionsSequence,
  viewDataById,
} from '../../services/quiz.service';
import { formatTimestamp } from '../../utils/datetime.util';
import Iconify from '../common/iconify.component';
import ConfirmPopup from '../common/confirm-popup.component';
import { ACTIONS, CONTENT_ALIAS } from '../../config/const.config';
import AddEditQuizQuestion from './add-edit-question.component';
import handleHTMLContent from '../../utils/html-parser.util';

const ManageQuestions = ({ id }) => {
  const quiz = useParams();

  const { control, setValue, getValues } = useForm({
    defaultValues: {
      questions: [],
    },
  });

  const {
    fields: questionsFields,
    append: questionsAppend,
    update: questionsUpdate,
    remove: questionsRemove,
    move: questionMove,
  } = useFieldArray({
    control,
    name: 'questions',
  });

  const [quizDetail, setQuizDetail] = useState({});
  const sequenceRef = useRef({
    initial: [],
    updated: [],
  });
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });

  const selectedQuestionRef = useRef({ index: null, questionId: null });
  const [showConfirmDailog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const handleActionSelect = (selectedAction, selectedData) => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    setDoAction({
      ...doAction,
      data: selectedData || null,
      action: selectedAction || null,
    });
  };

  const handleActionCancel = () => {
    setDoAction({
      ...doAction,
      data: null,
      action: null,
    });
  };

  const handleActionSuccess = (message = '', data = null, index = null) => {
    setSnackbarInfo({ ...snackbarInfo, show: false });

    if (data) {
      if (index !== null) {
        questionsUpdate(index, data);
      } else {
        questionsAppend(data);
      }
    }

    if (message) {
      setTimeout(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message,
        });
      }, 10);
    }

    handleActionCancel();
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    questionMove(result.source.index, result.destination.index);

    const tempSourceDisplayOrder =
      sequenceRef.current.updated[result.source.index];
    sequenceRef.current.updated[result.source.index] =
      sequenceRef.current.updated[result.destination.index];
    sequenceRef.current.updated[result.destination.index] =
      tempSourceDisplayOrder;
  };

  const handleSaveOrder = () => {
    const questionIds = questionsFields.map((item) => item.questionId);
    manageQuizQuestionsSequence({ questionIds })
      .then((res) => {
        if (res.data.success) {
          sequenceRef.current.initial = [...sequenceRef.current.updated];
          handleActionSuccess('Questions sequence saved successfully.');
        }
      })
      .catch((err) => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message:
            err.response?.data?.message ||
            'Something went wrong, please try again.',
        });
      });
  };

  const handleDeleteQuestion = () => {
    if (selectedQuestionRef.current.questionId) {
      deleteQuizQuestionById(selectedQuestionRef.current.questionId)
        .then(() => {
          questionsRemove(selectedQuestionRef.current.index);
          handleActionSuccess('Question deleted successfully.');
          setShowConfirmDialog(false);
          selectedQuestionRef.current.index = null;
          selectedQuestionRef.current.questionId = null;
        })
        .catch((err) => {
          const message =
            err.response?.data?.message ||
            'Something went wrong, please try again.';

          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'error',
            message,
          });
        });
    } else {
      questionsRemove(selectedQuestionRef.current.index);
      handleActionSuccess('Question deleted successfully.');
      setShowConfirmDialog(false);
      selectedQuestionRef.current.index = null;
      selectedQuestionRef.current.questionId = null;
    }
  };

  useEffect(() => {
    viewDataById(id || quiz.id)
      .then((res) => {
        setQuizDetail(res.data);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });

    getQuestionsByQuizId(id || quiz.id)
      .then((res) => {
        if (res.data.length > 0) {
          setValue('questions', res.data);
          sequenceRef.current.initial = res.data?.map(
            (item, index) => item.sequence || index + 1
          );
          sequenceRef.current.updated = res.data?.map(
            (item, index) => item.sequence || index + 1
          );
        }
        setLoading(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });
  }, [id]);

  return (
    <>
      {loading && <Loading />}

      {!loading && (
        <>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 1, py: 2 }}
          >
            <Link to={ROUTE_PATH.QUIZ}>
              <Button
                color="inherit"
                variant="contained"
                startIcon={<Iconify icon="ic:arrow-back" />}
              >
                Back
              </Button>
            </Link>

            <Stack direction="row" spacing={1}>
              <Box>
                {JSON.stringify(sequenceRef.current.initial) !==
                  JSON.stringify(sequenceRef.current.updated) && (
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={handleSaveOrder}
                  >
                    Save Sequence
                  </Button>
                )}
              </Box>
              <Button
                color="primary"
                variant="contained"
                onClick={() => handleActionSelect(ACTIONS.CREATE.value)}
              >
                Add {CONTENT_ALIAS.QUIZ_QUESTION}
              </Button>
            </Stack>
          </Stack>

          <Box px={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4} md={4}>
                <Card sx={{ minWidth: '90%', height: 'max-content' }}>
                  <CardContent>
                    <Typography
                      sx={{ fontSize: 14, fontWeight: 500 }}
                      color="secondary"
                    >
                      Name:
                    </Typography>
                    <Typography variant="h6" component="div" marginBottom={1}>
                      {quizDetail?.title}
                    </Typography>

                    <Typography
                      sx={{ fontSize: 14, fontWeight: 500 }}
                      color="secondary"
                    >
                      Description:
                    </Typography>
                    <Typography variant="body2" marginBottom={1}>
                      {quizDetail?.description}
                    </Typography>

                    <Typography
                      sx={{ fontSize: 14, fontWeight: 500 }}
                      color="secondary"
                      marginBottom={1}
                    >
                      <span>Pass Criteria: </span>
                      <Typography
                        variant="body2"
                        color="black"
                        display="inline"
                      >
                        {quizDetail?.passCriteria || 0} %
                      </Typography>
                    </Typography>

                    <Typography
                      sx={{ fontSize: 14, fontWeight: 500 }}
                      color="secondary"
                      marginBottom={1}
                    >
                      <span>Allowed Attempts: </span>
                      <Typography
                        variant="body2"
                        color="black"
                        display="inline"
                      >
                        {quizDetail?.allowedAttempts || 1}
                      </Typography>
                    </Typography>

                    <Typography
                      sx={{ fontSize: 14, fontWeight: 500 }}
                      color="secondary"
                    >
                      <span>Date: </span>
                      <Typography
                        variant="body2"
                        color="black"
                        display="inline"
                      >
                        {formatTimestamp(
                          quizDetail?.startTime,
                          'DD-MM-YYYY'
                        ).replaceAll('-', '.')}{' '}
                        -{' '}
                        {formatTimestamp(
                          quizDetail?.endTime,
                          'DD-MM-YYYY'
                        ).replaceAll('-', '.')}
                      </Typography>
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={8} md={8}>
                {questionsFields.length === 0 && (
                  <Card
                    sx={{
                      minWidth: '100%',
                      marginBottom: 1,
                    }}
                  >
                    <CardContent>
                      <Typography textAlign="center">
                        No questions added.
                      </Typography>
                    </CardContent>
                  </Card>
                )}

                {questionsFields.length > 0 && (
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="questions" isDropDisabled={false}>
                      {(provided) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                        >
                          {questionsFields.map((field, index) => (
                            <Draggable
                              key={`question-${field.id}`}
                              draggableId={field.id.toString()}
                              index={index}
                            >
                              {(provide) => (
                                <Card
                                  key={field.id}
                                  sx={{ minWidth: '100%', marginBottom: 1 }}
                                  ref={provide.innerRef}
                                  {...provide.draggableProps}
                                  {...provide.dragHandleProps}
                                >
                                  <CardContent
                                    sx={{
                                      padding: '20px !important',
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <IconButton
                                      aria-label="order"
                                      size="small"
                                      sx={{
                                        mr: 1.5,
                                        cursor: '-webkit-grab',
                                      }}
                                      disableRipple
                                    >
                                      <Iconify icon="ic:round-table-rows" />
                                    </IconButton>

                                    <Stack
                                      direction="row"
                                      justifyContent="space-between"
                                      alignItems="center"
                                      width="100%"
                                    >
                                      <Typography
                                        dangerouslySetInnerHTML={{
                                          __html: `<div><b>Question ${
                                            index + 1
                                          }:</b></div>${handleHTMLContent(
                                            getValues(
                                              `questions[${index}].questionText`
                                            ),
                                            true
                                          )}`,
                                        }}
                                      />
                                    </Stack>

                                    <IconButton
                                      aria-label="edit"
                                      size="small"
                                      onClick={() =>
                                        handleActionSelect(
                                          ACTIONS.EDIT.value,
                                          getValues(`questions[${index}]`)
                                        )
                                      }
                                    >
                                      <Iconify icon="mdi:square-edit-outline" />
                                    </IconButton>
                                  </CardContent>
                                </Card>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
              </Grid>
            </Grid>
          </Box>
        </>
      )}

      {doAction.action === ACTIONS.CREATE.value && (
        <AddEditQuizQuestion
          title={`Add New ${CONTENT_ALIAS.QUIZ_QUESTION}`}
          dataId={0}
          quizId={id || quiz.id}
          onCancel={handleActionCancel}
          onSuccess={(message, data) => handleActionSuccess(message, data)}
        />
      )}

      {doAction.action === ACTIONS.EDIT.value && (
        <AddEditQuizQuestion
          title={`Edit ${CONTENT_ALIAS.QUIZ_QUESTION} - ${doAction.data.questionText}`}
          dataId={doAction.data.questionId}
          quizId={id || quiz.id}
          onCancel={handleActionCancel}
          onSuccess={(message, data) => {
            handleActionSuccess(
              message,
              data,
              questionsFields.findIndex(
                (question) => question.questionId === data.questionId
              )
            );
          }}
          showDeletePrompt={(questionId) => {
            setShowConfirmDialog(true);
            selectedQuestionRef.current.questionId = questionId;
            selectedQuestionRef.current.index = questionsFields.findIndex(
              (question) => question.questionId === questionId
            );
          }}
        />
      )}

      {showConfirmDailog && (
        <ConfirmPopup
          title={`Delete ${CONTENT_ALIAS.QUIZ_QUESTION}`}
          message="Do you want to delete this question? You can not undo this action!"
          onCancel={() => {
            setShowConfirmDialog(false);
            selectedQuestionRef.current.index = null;
            selectedQuestionRef.current.questionId = null;
          }}
          onSuccess={handleDeleteQuestion}
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </>
  );
};

ManageQuestions.propTypes = {
  id: PropTypes.number.isRequired,
};

export default ManageQuestions;
